import axios from 'axios';
import { getApiRoot } from '@/lib/domainResolver';
import { setAuth, clearAuth } from '@rbac/permissions';

/**
 * Company sign-in for the multi-tenant foundation (server: routes/authRouter.js). Kept in
 * one module so the rest of the SPA stays mode-agnostic:
 *
 *  - `loadMode()` asks the server which sign-in to render (TENANCY=multi or legacy).
 *  - `adaptMe(me)` maps /api/auth/me onto the auth-cache shape the existing UI already
 *    reads (permissions as "module:action"), so Can/useCan/RequirePermission need no change.
 *  - `tenantHeaders()` is what lib/api.js attaches to every request in this mode: the
 *    session's CSRF token and the active branch (the server answers 409 if a tab is stale).
 *
 * The CSRF token and branch are kept in memory only; a reload re-reads them from /me.
 */

let mode = null;
let csrfToken = null;
let activeBranchId = null;

const client = axios.create({ baseURL: `${getApiRoot()}/api/auth`, withCredentials: true });
client.interceptors.request.use((config) => {
  Object.assign(config.headers, tenantHeaders(config.method));
  return config;
});

export function isMultiTenant() {
  return mode === true;
}

// Companies live on the platform and applications became branches: their legacy screens
// do not exist in multi-tenant mode (the server's catalog drops them too).
const RETIRED_MODULES = new Set(['companies', 'applications']);

/** Is this legacy module retired in the current mode? (routes + dashboard share it) */
export function isRetiredModule(moduleKey) {
  return isMultiTenant() && RETIRED_MODULES.has(moduleKey);
}

export async function loadMode() {
  if (mode !== null) return mode;
  try {
    mode = !!(await client.get('/mode')).data.multiTenant;
  } catch {
    mode = false;
  }
  return mode;
}

export function tenantHeaders(method = 'get') {
  if (!isMultiTenant()) return {};
  const headers = {};
  if (csrfToken && String(method).toLowerCase() !== 'get') headers['x-csrf-token'] = csrfToken;
  if (activeBranchId) headers['x-branch-id'] = activeBranchId;
  return headers;
}

export function errorOf(ex) {
  const data = ex && ex.response && ex.response.data;
  return { message: (data && data.error) || (ex && ex.message) || 'Request failed', code: data && data.code, status: ex && ex.response && ex.response.status };
}

/** /api/auth/me → the cached auth object the UI gates on. */
export function adaptMe(me) {
  return {
    mode: 'multi',
    id: me.user.id,
    email: me.user.email,
    name: me.user.fullName,
    isAdmin: false,
    isTenantAdmin: me.isTenantAdmin,
    permissions: me.permissions.map((p) => p.replace('.', ':')),
    screens: me.screens,
    company: me.company,
    branches: me.branches,
    activeBranchId: me.activeBranchId,
    authStrength: me.authStrength,
    hasTotp: me.hasTotp,
    needsStrongFactor: me.isTenantAdmin && me.authStrength !== 'strong-step-up',
  };
}

/** Refresh the cached profile from the server; clears it when signed out. */
export async function refreshMe() {
  try {
    const me = (await client.get('/me')).data;
    csrfToken = me.csrfToken;
    activeBranchId = me.activeBranchId;
    const auth = adaptMe(me);
    setAuth(auth);
    return auth;
  } catch (ex) {
    if (errorOf(ex).status === 401) {
      csrfToken = null;
      activeBranchId = null;
      clearAuth();
      return null;
    }
    throw ex;
  }
}

function remember(data) {
  if (data && data.csrfToken) csrfToken = data.csrfToken;
  if (data && data.activeBranchId) activeBranchId = data.activeBranchId;
  return data;
}

export async function resolveCompany(slug) {
  return (await client.post('/company/resolve', { slug })).data;
}

export async function passwordLogin(preauthId, identifier, password) {
  return remember((await client.post('/password/login', { preauthId, identifier, password })).data);
}

export async function completeMfa(preauthId, ticket, code) {
  return remember((await client.post('/mfa', { preauthId, ticket, code })).data);
}

// ---- phone, MPIN, devices, recovery (routes/authRouter.js §phone)

/** purpose: 'mpin_enroll' | 'mpin_reset' | 'device_enroll' */
export async function startPhoneChallenge(preauthId, phone, purpose) {
  return (await client.post('/phone/challenges', { preauthId, phone, purpose })).data;
}

/** proof: {code} for the code provider, {idToken} for Firebase → {capability} */
export async function verifyPhoneChallenge(preauthId, challengeId, phone, purpose, proof) {
  return (await client.post(`/phone/challenges/${encodeURIComponent(challengeId)}/verify`, { preauthId, phone, purpose, ...proof })).data;
}

export async function mpinLogin(preauthId, phone, mpin) {
  return remember((await client.post('/mpin/login', { preauthId, phone, mpin })).data);
}

export async function mpinEnroll(preauthId, capability, mpin, mpinConfirm, deviceLabel) {
  return remember((await client.post('/mpin/enroll', { preauthId, capability, mpin, mpinConfirm, deviceLabel })).data);
}

export async function mpinReset(preauthId, capability, mpin, mpinConfirm, deviceLabel) {
  return remember((await client.post('/mpin/reset', { preauthId, capability, mpin, mpinConfirm, deviceLabel })).data);
}

export async function enrollDevice(preauthId, capability, phone, mpin, deviceLabel) {
  return remember((await client.post('/devices/enroll', { preauthId, capability, phone, mpin, deviceLabel })).data);
}

export async function forgotPassword(preauthId, identifier) {
  return (await client.post('/password/forgot', { preauthId, identifier })).data;
}

export async function resetPassword(preauthId, token, password) {
  return (await client.post('/password/reset', { preauthId, token, password })).data;
}

export async function describeInvitation(preauthId, token) {
  return (await client.get(`/invitations/${encodeURIComponent(token)}`, { params: { preauthId } })).data;
}

export async function acceptInvitation(preauthId, token, fullName, password) {
  return remember((await client.post('/invitations/accept', { preauthId, token, fullName, password })).data);
}

export async function startTotp() {
  return (await client.post('/totp/start')).data;
}

export async function confirmTotp(code) {
  return (await client.post('/totp/confirm', { code })).data;
}

/** Switch branch, then hard-reload: no prior-branch data may survive into the new branch. */
export async function switchBranch(branchId) {
  await client.post('/active-branch', { branchId });
  window.location.assign('/');
}

export async function tenantLogout() {
  try {
    await client.post('/logout');
  } finally {
    csrfToken = null;
    activeBranchId = null;
    clearAuth();
  }
}

const adminClient = axios.create({ baseURL: `${getApiRoot()}/api/admin`, withCredentials: true });
adminClient.interceptors.request.use((config) => {
  Object.assign(config.headers, tenantHeaders(config.method));
  return config;
});

/** Company administration API (routes/adminRouter.js). */
export async function adminRequest(method, path, body) {
  return (await adminClient.request({ method, url: path, data: body })).data;
}
